import React, { useMemo, useState, useRef, useEffect } from "react";
import { Modal } from "react-bootstrap";
import styled from "styled-components";
import Button from "src/components/Button/Button";
import ReactMarkdown from "react-markdown";
import ResponsiveImage from "src/components/ImageWrapper/ResponsiveImage";
import wheelConfigDefault from "src/config/wheelConfigDefault";

// Utility: pick a weighted random index given an array of weights
function weightedRandomIndex(weights) {
  const total = weights.reduce((a, b) => a + (b > 0 ? b : 0), 0);
  if (total <= 0) return Math.floor(Math.random() * weights.length);
  let r = Math.random() * total;
  for (let i = 0; i < weights.length; i += 1) {
    const w = weights[i] > 0 ? weights[i] : 0;
    if (r < w) return i;
    r -= w;
  }
  return weights.length - 1;
}

// Utility: build an SVG wedge path
function describeWedge(cx, cy, r, startAngle, endAngle) {
  const rad = Math.PI / 180;
  const sx = cx + r * Math.cos(rad * startAngle);
  const sy = cy + r * Math.sin(rad * startAngle);
  const ex = cx + r * Math.cos(rad * endAngle);
  const ey = cy + r * Math.sin(rad * endAngle);
  const largeArc = endAngle - startAngle <= 180 ? 0 : 1;
  return `M ${cx} ${cy} L ${sx} ${sy} A ${r} ${r} 0 ${largeArc} 1 ${ex} ${ey} Z`;
}

// Build an SVG arc path between two angles at radius r
function describeArcPath(cx, cy, r, startAngle, endAngle, sweepCW = true) {
  const rad = Math.PI / 180;
  const sx = cx + r * Math.cos(rad * startAngle);
  const sy = cy + r * Math.sin(rad * startAngle);
  const ex = cx + r * Math.cos(rad * endAngle);
  const ey = cy + r * Math.sin(rad * endAngle);
  let delta = (((endAngle - startAngle) % 360) + 360) % 360;
  if (!sweepCW) delta = 360 - delta;
  const largeArc = delta > 180 ? 1 : 0;
  const sweep = sweepCW ? 1 : 0;
  return `M ${sx} ${sy} A ${r} ${r} 0 ${largeArc} ${sweep} ${ex} ${ey}`;
}

const VIEWBOX = 500;
const CENTER = VIEWBOX / 2;
const RADIUS = 220;

const SpinWheelModal = ({
  show,
  onHide,
  onPrize,
  subscribeOnResult = false,
  data: inlineWheelData,
  repeat = 1,
  autoSpin = true,
}) => {
  // Get wheel data: merge with defaults so custom data can override but UI text fields are always present
  const data = useMemo(
    () => {
      if (!inlineWheelData) return wheelConfigDefault;
      return {
        ...wheelConfigDefault,
        ...inlineWheelData,
      };
    },
    [inlineWheelData]
  );
  const segments = data?.segmentValuesArray || [];
  const repeatedSegments = useMemo(() => {
    const times = Math.max(1, Math.floor(Number(repeat) || 1));
    if (times === 1) return segments;
    const out = [];
    for (let r = 0; r < times; r += 1) out.push(...segments);
    return out;
  }, [segments, repeat]);
  const colors = data?.colorArray || ["#e74c3c", "#2c3e50"];
  const maxSpins = Math.max(1, data?.numSpins || 1);

  // Scale values from wheelData (typically authored for a larger canvas)
  const DIAMETER = RADIUS * 2;
  const wheelSize =
    Number(data?.wheelSize) > 0 ? Number(data.wheelSize) : DIAMETER;
  const scale = DIAMETER / wheelSize; // scale authored px to our SVG

  // Visual styles from data (with safe fallbacks)
  const wheelStrokeColor = data?.wheelStrokeColor;
  const wheelStrokeWidth = (Number(data?.wheelStrokeWidth) * scale * 0.4); // soften for smaller canvas
  const segmentStrokeColor = data?.segmentStrokeColor;
  const segmentStrokeWidth = (Number(data?.segmentStrokeWidth)) * scale;
  const centerCircleFillColor = data?.centerCircleFillColor;
  const centerCircleStrokeColor = data?.centerCircleStrokeColor;
  const centerCircleStrokeWidth =
    (Number(data?.centerCircleStrokeWidth)) * scale;
  const centerCircleRadius = Math.max(
    28,
    ((Number(data?.centerCircleSize)) * scale) / 2
  );
  const wheelTextColor = data?.wheelTextColor;
  const wheelTextOffsetY =
    (Number(data?.wheelTextOffsetY)) * scale; // distance from rim inward

  const overlayVideoSrc2 = "/videos/confetti-2.mp4";

  function toPxSize(spec, fallbackPx = 16) {
    if (!spec || typeof spec !== "string")
      return Math.max(10, Math.round(fallbackPx * scale));
    const n = parseFloat(spec);
    if (Number.isNaN(n)) return Math.max(10, Math.round(fallbackPx * scale));
    if (spec.endsWith("em")) return Math.max(10, Math.round(n * 16 * scale));
    if (spec.endsWith("px")) return Math.max(10, Math.round(n * scale));
    return Math.max(10, Math.round(n * scale));
  }
  const wheelTextFontSizeRaw = toPxSize(
    String(data?.wheelTextSize || "16px"),
    16
  );
  const wheelTextFontSize = Math.min(
    Math.max(12, wheelTextFontSizeRaw),
    Math.round(RADIUS * 0.18) // clamp to ~18% of radius to avoid clipping
  );

  function uprightTextRotation(midAngle) {
    // Base rotation to align text along radius
    let rot = midAngle - 90;
    // Keep text upright: if upside down (facing bottom), flip 180deg
    const norm = ((rot % 360) + 360) % 360;
    if (norm > 90 && norm < 270) rot += 180;
    return rot;
  }

  const [rotation, setRotation] = useState(0);
  const [spinsUsed, setSpinsUsed] = useState(0);
  const [spinning, setSpinning] = useState(false);
  const [resultMsg, setResultMsg] = useState("");
  const [prizeValue, setPrizeValue] = useState(null);
  const [pointerFlex, setPointerFlex] = useState(false);
  const [hasAutoSpun, setHasAutoSpun] = useState(false);
  const [showConfetti, setShowConfetti] = useState(false);

  const audioContextRef = useRef(null);
  const wheelRef = useRef(null);
  const animationFrameRef = useRef(null);
  const lastSegmentRef = useRef(-1);
  const spinStartTimeRef = useRef(0);
  const targetRotationRef = useRef(0);
  const initialRotationRef = useRef(0);

  const canSpin = !spinning && spinsUsed < maxSpins;

  // Auto-spin when modal opens (if autoSpin is enabled)
  useEffect(() => {
    if (autoSpin && show && !hasAutoSpun && canSpin && repeatedSegments.length > 0) {
      // Small delay to let modal render
      const timer = setTimeout(() => {
        spin();
        setHasAutoSpun(true);
      }, 300);
      return () => clearTimeout(timer);
    }
  }, [show, hasAutoSpun, autoSpin]);

  // Reset auto-spin flag and confetti when modal closes
  useEffect(() => {
    if (!show) {
      setHasAutoSpun(false);
      setShowConfetti(false);
      setResultMsg("");
    }
  }, [show]);

  // Initialize Web Audio Context for tick sound
  useEffect(() => {
    if (typeof window !== 'undefined' && window.AudioContext) {
      audioContextRef.current = new (window.AudioContext || window.webkitAudioContext)();
    }
    return () => {
      if (audioContextRef.current) {
        audioContextRef.current.close();
      }
    };
  }, []);

  // Generate and play tick sound
  const playTickSound = (intensity = 1) => {
    if (!audioContextRef.current) return;

    try {
      const ctx = audioContextRef.current;
      const oscillator = ctx.createOscillator();
      const gainNode = ctx.createGain();

      oscillator.connect(gainNode);
      gainNode.connect(ctx.destination);

      // Sharp click sound
      oscillator.type = 'square';
      oscillator.frequency.setValueAtTime(800, ctx.currentTime);

      // Volume based on intensity (wheel speed)
      const volume = 0.1 * Math.min(1, intensity);
      gainNode.gain.setValueAtTime(volume, ctx.currentTime);
      gainNode.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + 0.05);

      oscillator.start(ctx.currentTime);
      oscillator.stop(ctx.currentTime + 0.05);
    } catch (err) {
      console.warn('Audio playback failed:', err);
    }
  };

  // Monitor wheel rotation during spin and trigger tick sounds/animation
  const monitorWheelRotation = useRef();

  monitorWheelRotation.current = () => {
    if (!spinning || !wheelRef.current) return;

    const currentTime = Date.now();
    const elapsed = currentTime - spinStartTimeRef.current;
    const duration = 6000; // matches CSS transition duration (6 seconds)

    if (elapsed >= duration) {
      // Spin complete
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
        animationFrameRef.current = null;
      }
      lastSegmentRef.current = -1;
      return;
    }

    // Easing function to match CSS cubic-bezier(0.22, 1, 0.36, 1) - ease-out
    const progress = elapsed / duration;
    // This approximates the bezier curve for ease-out behavior
    const eased = 1 - Math.pow(1 - progress, 3);

    // Calculate current rotation using initial rotation + progress
    const rotationDelta = targetRotationRef.current - initialRotationRef.current;
    const currentRotation = initialRotationRef.current + (rotationDelta * eased);
    const normalizedRotation = ((currentRotation % 360) + 360) % 360;

    // Calculate current segment
    const slice = 360 / Math.max(1, repeatedSegments.length);
    const pointerAngle = 270; // top position in SVG space
    const angleAtTop = (pointerAngle - normalizedRotation + 360) % 360;
    const currentSegment = Math.floor(angleAtTop / slice) % Math.max(1, repeatedSegments.length);

    // Segment changed - trigger tick
    if (currentSegment !== lastSegmentRef.current && lastSegmentRef.current !== -1) {
      // Calculate intensity - high at start (fast spinning), low at end (slow)
      // Use progress directly since easing makes early progress slower
      const intensity = 1 - progress;

      // Play sound
      playTickSound(intensity);

      // Flex pointer
      setPointerFlex(true);
      setTimeout(() => setPointerFlex(false), 80);
    }

    lastSegmentRef.current = currentSegment;
    animationFrameRef.current = requestAnimationFrame(monitorWheelRotation.current);
  };

  // Cleanup animation frame on unmount
  useEffect(() => {
    return () => {
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
    };
  }, []);

  function spin() {
    if (!canSpin || repeatedSegments.length === 0) return;
    setResultMsg("");
    const weights = repeatedSegments.map((s) =>
      typeof s.probability === "number" ? s.probability : 1
    );
    const index = weightedRandomIndex(weights);
    const slice = 360 / repeatedSegments.length;
    const segmentCenterAngle = index * slice + slice / 2; // degrees
    // Rotate so that the segmentCenterAngle ends at the top (270deg in SVG y-down coords)
    const pointerAngle = -90; // top in CSS/SVG coordinates
    const spins = 3 + Math.floor(Math.random() * 3); // 3-5 full spins
    const target = spins * 360 + (pointerAngle - segmentCenterAngle);

    setSpinning(true);
    setSpinsUsed((n) => n + 1);

    // Track rotation values for animation monitoring
    initialRotationRef.current = rotation;
    targetRotationRef.current = rotation + target;
    spinStartTimeRef.current = Date.now();
    lastSegmentRef.current = -1;

    // Animate via CSS transition by updating rotation state
    setRotation((prev) => prev + target);

    // Start monitoring rotation for tick sounds
    if (animationFrameRef.current) {
      cancelAnimationFrame(animationFrameRef.current);
    }
    // Small delay to ensure state updates have processed
    setTimeout(() => {
      animationFrameRef.current = requestAnimationFrame(monitorWheelRotation.current);
    }, 50);
  }

  const labels = repeatedSegments.map((s) =>
    typeof s.value === "string" ? s.value : String(s.value)
  );

  // Get UI text from config
  const modalTitle = data.modalTitle;
  const spinButtonText = data.spinButtonText;
  const closeButtonText = data.closeButtonText;
  const spinsLeftText = data.spinsLeftText
    .replace("{current}", String(maxSpins - spinsUsed))
    .replace("{total}", String(maxSpins));
  const disabledText = data.disabledText;

  return (
    <StyledModal
      show={show}
      onHide={onHide}
      centered
      backdrop="static"
      keyboard={false}
    >
      <Modal.Header closeButton>
        <Modal.Title>
          {modalTitle}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {/* Background image layer */}
        <div className="bgImageLayer" />
        {/* Confetti video overlay - only shows on win */}
        {showConfetti && (
          <video
            autoPlay
            muted
            loop
            playsInline
            className="overlay-video confetti-video"
            key="confetti-video"
          >
            <source src={overlayVideoSrc2} type="video/mp4" />
          </video>
        )}
        <div className="wheelContainer">
          <div className={`pointer ${pointerFlex ? 'flex' : ''}`} aria-hidden />
          <svg className="wheelSVG" viewBox={`0 0 ${VIEWBOX} ${VIEWBOX}`}>
            <defs>
              {repeatedSegments.map((seg, i) => {
                const start = (i * 360) / repeatedSegments.length;
                const end = ((i + 1) * 360) / repeatedSegments.length;
                // Path radius just inside rim for label path
                const offsetFromRim = Math.min(
                  RADIUS * 0.45,
                  Math.max(18, wheelTextOffsetY)
                );
                const textRadius = Math.max(
                  centerCircleRadius + 24,
                  RADIUS - offsetFromRim
                );
                // Create arc path for text - simple clockwise arc from start to end
                const path = describeArcPath(
                  CENTER,
                  CENTER,
                  textRadius,
                  start,
                  end,
                  true  // always sweep clockwise
                );
                return (
                  <path
                    key={`p-${i}`}
                    id={`seg-arc-${i}`}
                    d={path}
                    fill="none"
                  />
                );
              })}
            </defs>
            <g
              ref={wheelRef}
              className="wheel"
              style={{
                transform: `rotate(${rotation}deg)`,
                transformOrigin: `${CENTER}px ${CENTER}px`,
              }}
              onTransitionEnd={async () => {
                if (!spinning) return;
                // Compute final segment based on absolute rotation angle
                const norm = ((rotation % 360) + 360) % 360; // 0..359
                const pointerAngle = 270; // top in SVG space (because 0deg is along +X, 90 is +Y)
                const angleAtTop = (pointerAngle - norm + 360) % 360; // 0..360 where 0 is first segment start
                const slice = 360 / Math.max(1, repeatedSegments.length);
                const index =
                  Math.floor(angleAtTop / slice) %
                  Math.max(1, repeatedSegments.length);
                const seg = repeatedSegments[index] || {};
                const prize = seg?.userData?.prize;
                const msg = seg?.resultText;
                const isWin = seg?.win === true;
                setSpinning(false);
                setResultMsg(msg);
                setPrizeValue(typeof prize !== "undefined" ? prize : null);

                // Show confetti if it's a win, then wait before callback
                if (isWin) {
                  setShowConfetti(true);
                  // Small delay to let confetti video start before callback/alert
                  setTimeout(() => {
                    try {
                      onPrize?.(prize);
                    } catch {}
                  }, 300);
                } else {
                  // No confetti, fire callback immediately
                  try {
                    onPrize?.(prize);
                  } catch {}
                }
              }}
            >
              {/* Outer ring */}
              <circle
                cx={CENTER}
                cy={CENTER}
                r={RADIUS + wheelStrokeWidth}
                fill="none"
                stroke={wheelStrokeColor}
                strokeWidth={wheelStrokeWidth}
              />

              {repeatedSegments.map((seg, i) => {
                const start = (i * 360) / repeatedSegments.length;
                const end = ((i + 1) * 360) / repeatedSegments.length;
                const mid = (start + end) / 2;
                const fill = colors[i % colors.length];
                // Place text closer to rim while ensuring it's not hidden by the hub
                const offsetFromRim = Math.min(
                  RADIUS * 0.45,
                  Math.max(18, wheelTextOffsetY)
                );
                const textRadius = Math.max(
                  centerCircleRadius + 24,
                  RADIUS - offsetFromRim
                );
                const angle = end - start;
                const arcLen = 2 * Math.PI * textRadius * (angle / 360);
                const textRotation = uprightTextRotation(mid);
                // Estimate text width and scale down if needed to fit the arc comfortably
                const label = labels[i] || "";
                const estCharWidth = 0.6 * wheelTextFontSize; // rough average
                const estLabelWidth = estCharWidth * String(label).length;
                const maxLabelWidth = arcLen * 0.75; // allow up to 75% of arc
                const scaleFactor = Math.min(
                  1,
                  maxLabelWidth / Math.max(1, estLabelWidth)
                );
                const labelFontSize = Math.max(
                  10,
                  Math.round(wheelTextFontSize * scaleFactor)
                );
                return (
                  <g key={i}>
                    <path
                      d={describeWedge(CENTER, CENTER, RADIUS, start, end)}
                      fill={fill}
                      stroke={segmentStrokeColor}
                      strokeWidth={segmentStrokeWidth}
                    />
                    <text
                      fill={wheelTextColor}
                      fontSize={labelFontSize}
                      fontWeight="700"
                      stroke="rgba(0,0,0,0.35)"
                      strokeWidth={1}
                      paintOrder="stroke fill"
                    >
                      <textPath
                        xlinkHref={`#seg-arc-${i}`}
                        startOffset="50%"
                        textAnchor="middle"
                        dominantBaseline="central"
                      >
                        {label}
                      </textPath>
                    </text>
                  </g>
                );
              })}
              {/* Inner hub */}
              <circle
                cx={CENTER}
                cy={CENTER}
                r={centerCircleRadius}
                fill={centerCircleFillColor}
                stroke={centerCircleStrokeColor}
                strokeWidth={centerCircleStrokeWidth}
              />
            </g>
          </svg>
          {/* Logo in center */}
          <div className="wheelLogo">
            <ResponsiveImage
              src="logo/PLAYGROUND-SPADE.png"
              alt="Playground Logo"
              customStyles={{
                width: "100%",
                height: "100%",
                objectFit: "contain",
              }}
            />
          </div>
          {resultMsg ? (
            <div className="toast">
              <p>{resultMsg}</p>
            </div>
          ) : null}
          {resultMsg ? (
            <div className="resultBanner" role="status" aria-live="polite">
              <ReactMarkdown>{resultMsg}</ReactMarkdown>
            </div>
          ) : null}
          {!resultMsg && spinsUsed === 0 && data?.introText ? (
            <div
              className="intro"
              style={{
                top: `${CENTER - Math.min(centerCircleRadius * 0.6, 80)}px`,
                width: `${Math.round(centerCircleRadius * 1.6)}px`,
              }}
              dangerouslySetInnerHTML={{ __html: String(data.introText) }}
            />
          ) : null}
        </div>
        {!autoSpin && !resultMsg && (
          <div className="spinControls">
            <Button
              onClick={spin}
              disabled={!canSpin}
              label={spinButtonText}
              size="large"
              width="wide"
            />
            {maxSpins > 1 && (
              <span className="spinsLeft">
                {spinsLeftText}
              </span>
            )}
          </div>
        )}
        {!autoSpin && !canSpin && !resultMsg && (
          <p className="disabledText">
            {disabledText}
          </p>
        )}
      </Modal.Body>
    </StyledModal>
  );
};

const StyledModal = styled(Modal)`
  z-index: 9999;
  .modal-header {
    background: linear-gradient(135deg, #d4af37 0%, #f4e4a6 50%, #d4af37 100%);
    .modal-title {
      color: var(--lightblack);
      font-weight: 700;
      text-align: center;
    }
  }
  .modal-content,
  .modal-header {
    border-radius: 0;
  }
  .modal-body {
    background-color: #000;
    position: relative;
    overflow: hidden;
  }

  /* Background image layer */
  .bgImageLayer {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: url(https://assets.playground.ca/pgfe/img/fit-in/2000x800/egd/game-list/featured-games-hero-bg.jpg) center 90% / cover no-repeat;
    opacity: 0.4;
    z-index: 0;
  }

  /* Video overlays */
  .overlay-video {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    object-fit: cover;
    z-index: 3;
    pointer-events: none;
    mix-blend-mode: screen;

    &.confetti-video {
      opacity: 1;
    }
  }

  .wheelContainer {
    position: relative;
    min-height: 420px;
    display: grid;
    place-items: center;
    z-index: 2;
  }
  .wheelSVG {
    width: 100%;
    height: 360px;
  }
  .wheelLogo {
    position: absolute;
    width: 100px;
    height: 100px;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    pointer-events: none;
    z-index: 1;
  }
  .pointer {
    position: absolute;
    top: 30px;
    left: 50%;
    width: 24px;
    height: 30px;
    transform: translateX(-50%);
    transform-origin: center top;
    z-index: 2;
    transition: transform 0.08s ease-out;

    /* Create a flapper/ticker shape */
    &::before {
      content: '';
      position: absolute;
      bottom: 0;
      left: 0;
      width: 100%;
      height: 100%;
      background: linear-gradient(135deg, #ff4444 0%, #cc0000 100%);
      clip-path: polygon(50% 100%, 0% 0%, 100% 0%);
      box-shadow: 0 2px 4px rgba(0, 0, 0, 0.3),
                  inset -2px -2px 4px rgba(0, 0, 0, 0.2),
                  inset 2px 2px 4px rgba(255, 255, 255, 0.3);
    }

    /* Add highlight for 3D effect */
    &::after {
      content: '';
      position: absolute;
      bottom: 0;
      left: 25%;
      width: 50%;
      height: 60%;
      background: linear-gradient(to bottom, rgba(255, 255, 255, 0.4), transparent);
      clip-path: polygon(50% 100%, 0% 0%, 100% 0%);
      pointer-events: none;
    }
  }

  .pointer.flex {
    transform: translateX(-50%) rotate(10deg);
  }
  .wheel {
    transition: transform 6s cubic-bezier(0.22, 1, 0.36, 1);
  }
  .toast {
    position: absolute;
    bottom: 8px;
    left: 50%;
    transform: translateX(-50%);
    background: rgba(0, 0, 0, 0.7);
    padding: 8px 12px;
    border-radius: 4px;
    color: #fff;
  }
  .intro {
    position: absolute;
    top: 62px;
    left: 50%;
    transform: translateX(-50%);
    text-align: center;
    color: #fff;
    font-weight: 700;
    line-height: 1.2;
    pointer-events: none;
    text-shadow: 0 1px 2px rgba(0, 0, 0, 0.6);
  }
  .intro br {
    content: "";
  }
  .spinControls {
    display: flex;
    gap: 12px;
    align-items: center;
    margin-top: 12px;
  }
  .spinsLeft {
    color: var(--mutedwhite);
    font-size: 0.9rem;
  }
  .disabledText {
    margin-top: 8px;
    color: var(--mutedwhite);
    font-size: 0.95rem;
    text-align: center;
  }
  .resultBanner {
    position: absolute;
    inset: 0;
    display: flex;
    flex-direction: column;
    justify-content: center;
    padding: 20px;
    z-index: 3;
  }

  .resultBanner {
    background: rgba(0, 0, 0, 0.85);
    color: #fff;
    border-radius: 8px;
    text-align: center;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
    p {
      margin: 0;
      font-size: 1.1rem;
      line-height: 1.5;
    }

    strong {
      font-size: 1.3rem;
      font-weight: 700;
      display: block;
      margin-bottom: 0.5rem;
  }

  }
  .resultPrize {
    margin-top: 6px;
    background: rgba(205, 0, 0, 0.9);
    color: #fff;
    padding: 6px 10px;
    border-radius: 6px;
    font-weight: 800;
    font-size: 1.25rem;
    text-align: center;
  }
`;

export default SpinWheelModal;
